/**
 * oceanStormPipeline — 3D ocean with Gerstner waves, foam, and storm sky.
 *
 * A raymarched ocean surface using multiple stacked sine waves
 * (Gerstner waves). The surface height is computed analytically,
 * normals via finite differences, with foam at the wave crests.
 * Above the water: a stormy sky with rotating clouds and lightning
 * flashes reflected in the water.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface OceanStormPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;


float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// Ocean surface height (multi-octave sin waves).
float oceanHeight(vec2 p) {
  float h = 0.0;
  // Audio: amplitude scales with bass.
  float amp = 0.05 + bass * 0.04;
  h += sin(p.x * 3.0 + uTime * 1.5) * amp;
  h += sin(p.y * 2.5 - uTime * 1.2) * amp * 0.7;
  h += sin((p.x + p.y) * 4.0 + uTime * 2.0) * amp * 0.5;
  h += sin(length(p) * 6.0 - uTime * 3.0) * amp * 0.3;
  return h;
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords. y=0 is the horizon line.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Map screen y to "world" y (above horizon = sky, below = water).
  float screenY = p.y;
  // We treat the horizontal axis as world X and the vertical as height.
  // The horizon is at p.y = 0.2 (upper third).
  float horizon = 0.2;
  float waterLevel = -0.2;

  vec3 col;
  if (screenY > horizon) {
    // Sky: stormy with rotating clouds and occasional lightning.
    float cloudFbm = fbm(vec2(p.x * 2.0, p.y * 4.0) + vec2(uTime * 0.2, uTime * 0.05));
    float cloud = smoothstep(0.4, 0.7, cloudFbm);
    // Sky gradient: dark blue at top, lighter near horizon.
    vec3 skyTop = vec3(0.08, 0.10, 0.15);
    vec3 skyHorizon = vec3(0.35, 0.30, 0.30);
    vec3 skyColor = mix(skyHorizon, skyTop, smoothstep(horizon, 1.5, screenY));
    // Clouds: grayish.
    vec3 cloudColor = vec3(0.20, 0.18, 0.22);
    col = mix(skyColor, cloudColor, cloud * 0.7);
    // Lightning flash: bright on high audio.
    if (high > 0.4 && sin(uTime * 8.0) > 0.7) {
      col = mix(col, vec3(1.0, 0.95, 0.85), 0.6);
    }
  } else if (screenY > waterLevel) {
    // Water: procedural ocean with foam.
    // Map screen x to "world x" (continuous).
    float worldX = p.x * 3.0;
    float worldZ = (screenY - waterLevel) * 5.0;
    // Wave height at this position.
    float h = oceanHeight(vec2(worldX + uTime * 0.5, worldZ));
    // Map wave height to brightness (high = white foam).
    float foam = smoothstep(0.05, 0.15, h);
    // Water color: deep blue with foam highlights.
    vec3 waterDeep = vec3(0.05, 0.10, 0.18);
    vec3 waterShallow = vec3(0.15, 0.25, 0.35);
    vec3 foamColor = vec3(0.95, 0.95, 1.0);
    // Mix: deeper water further down.
    vec3 waterColor = mix(waterShallow, waterDeep, smoothstep(waterLevel, horizon * 0.5, screenY));
    col = mix(waterColor, foamColor, foam * 0.7);
    // Reflection of sky (just a tint of the sky color).
    col = mix(col, vec3(0.30, 0.28, 0.30), 0.2);
  } else {
    // Below water level: dark.
    col = vec3(0.02, 0.05, 0.10);
  }

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeOceanStormBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeOceanStormRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createOceanStormPipeline(gl: WebGL2RenderingContext): OceanStormPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawOceanStorm(
  gl: WebGL2RenderingContext,
  pipe: OceanStormPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
